import { useForm } from 'react-hook-form';
import { taskStatusMocked } from '../../../../../../constants';
import styles from './FilterList.module.scss';
import { CheckBoxOption, RolePermissionChecker, Text } from 'components';
import { Button } from 'components/Button/Button';
import { appActions } from 'store/app/slice';
import { selectPatientPageFilters } from 'store/patients/selectors';
import { patientsActions } from 'store/patients/slice';
import { getClinics, getDoctors, getUsers } from 'store/users/selectors';
import { useDispatch, useSelector } from 'store/utils';
import { TreatmentPlanTask, TreatmentPlanType, UserRole } from 'types';
import { parseFormDataToRequestParams } from 'utils';

type Props = {
  onClose?(): void;
};

export const FilterList = ({ onClose }: Props) => {
  const dispatch = useDispatch();

  const users = useSelector(getUsers);
  const filters = useSelector(selectPatientPageFilters);
  const clinics = useSelector(getClinics);
  const doctors = useSelector(getDoctors);

  const { search, sort } = filters;

  const { register, handleSubmit } = useForm({
    defaultValues: {
      clinics: filters?.clinicIds ?? [],
      doctors: filters?.doctorIds ?? [],
      archived: filters?.isArchived,
      taskStatus: filters?.tasks ?? [],
      treatmentPlans: filters?.treatmentPlanTypes ?? [],
      isTaskBlocked: filters?.isTaskBlocked,
    },
  });

  const onSubmit = handleSubmit(async (formData) => {
    const filterData = { ...formData };

    if (filterData.treatmentPlans?.length) {
      !Array.isArray(filterData.taskStatus)
        ? (filterData.taskStatus = [TreatmentPlanTask.IN_TREATMENT])
        : !filterData.taskStatus?.includes(TreatmentPlanTask.IN_TREATMENT)
          ? filterData.taskStatus?.push(TreatmentPlanTask.IN_TREATMENT)
          : null;
    }

    const requestParams = parseFormDataToRequestParams(filterData, search, sort);

    dispatch(patientsActions.resetPatientCasesBeforeUpdate());
    dispatch(patientsActions.setPatientPageFilters(requestParams));
    if (onClose) {
      onClose();
    } else {
      dispatch(appActions.setIsModalOpened({ name: 'patientFilters', isOpened: false }));
    }
  });

  return (
    <form className={styles.wrapper}>
      <RolePermissionChecker rolesAllowedToView={[UserRole.ADMIN, UserRole.DOCTOR, UserRole.READ_ONLY]}>
        <div className={styles.filterBlock}>
          <Text type="overline" className={styles.filterBlockTitle}>
            Studios
          </Text>
          {clinics.map((el) => (
            <CheckBoxOption optionText={el.name} key={el.id} id={el.id} register={register('clinics')} />
          ))}
        </div>
      </RolePermissionChecker>
      <div className={styles.filterBlock}>
        <Text type="overline" className={styles.filterBlockTitle}>
          Doctors
        </Text>
        {doctors.map((el) => (
          <CheckBoxOption
            optionText={el.name}
            isChecked={filters?.doctorIds?.includes(el.id)}
            key={el.id}
            id={el.id}
            register={register('doctors')}
          />
        ))}
      </div>
      <div className={styles.filterBlock}>
        <Text type="overline" className={styles.filterBlockTitle}>
          Status
        </Text>
        {taskStatusMocked.map((el) => (
          <CheckBoxOption optionText={el.name} key={el.id} id={el.id} register={register('taskStatus')} />
        ))}
        <CheckBoxOption optionText="Archived" key="archive" id="archive" register={register('archived')} />
        <CheckBoxOption
          optionText="Out of treatment"
          key="isTaskBlocked"
          id="isTaskBlocked"
          register={register('isTaskBlocked')}
        />
      </div>
      <div className={styles.filterBlock}>
        <Text type="overline" className={styles.filterBlockTitle}>
          Treatment plan
        </Text>
        {Object.values(TreatmentPlanType)
          .filter((value) => value !== TreatmentPlanType.NONE)
          .map((treatmentPlan) => (
            <CheckBoxOption
              optionText={treatmentPlan.charAt(0).toUpperCase() + treatmentPlan.slice(1).toLowerCase()}
              key={treatmentPlan}
              id={treatmentPlan}
              register={register('treatmentPlans')}
            />
          ))}
      </div>
      <div className={styles.buttonWrapper}>
        <Button onClick={onSubmit} className={styles.button}>
          Save filters
        </Button>
      </div>
    </form>
  );
};
